/**
 * Keyboard Shortcut Helper Component
 * Displays available keyboard shortcuts in a dialog
 */

import { useState, forwardRef, useImperativeHandle } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Chip,
  Stack,
  Divider,
  Tooltip,
} from '@mui/material';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import CloseIcon from '@mui/icons-material/Close';
import { KEYBOARD_SHORTCUTS } from '../../hooks/useKeyboardShortcuts';
import { tokens } from '../../theme/tokens';

export interface KeyboardShortcutHelperRef {
  open: () => void;
  close: () => void;
}

const KeyboardShortcutHelper = forwardRef<KeyboardShortcutHelperRef, object>((_, ref) => {
  const [open, setOpen] = useState(false);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    open: () => setOpen(true),
    close: () => setOpen(false),
  }));

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <>
      {/* Trigger Button */}
      <Tooltip title="Keyboard Shortcuts (Shift + ?)">
        <IconButton
          onClick={handleOpen}
          sx={{
            width: 40,
            height: 40,
            color: tokens.colors.neutral[500],
            '&:hover': {
              backgroundColor: tokens.colors.neutral[100],
              color: tokens.colors.text.primary,
            },
          }}
        >
          <KeyboardIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Shortcuts Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <KeyboardIcon sx={{ color: tokens.colors.primary.main }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: tokens.colors.primary.main }}>
                Keyboard Shortcuts
              </Typography>
            </Stack>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Use these keyboard shortcuts to navigate faster and boost your productivity
          </Typography>

          {KEYBOARD_SHORTCUTS.map((category, index) => (
            <Box key={category.category} sx={{ mb: 3 }}>
              {/* Category Header */}
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 700,
                  color: '#374151',
                  mb: 1.5,
                  letterSpacing: '0.05em',
                }}
              >
                {category.category}
              </Typography>

              {/* Shortcuts List */}
              <Stack spacing={1.5}>
                {category.shortcuts.map((shortcut) => (
                  <Stack
                    key={shortcut.keys}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      py: 1,
                      px: 1.5,
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: '#F9FAFB',
                      },
                    }}
                  >
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {shortcut.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {shortcut.keys.split(' ').map((key, idx) => (
                        <Chip
                          key={idx}
                          label={key}
                          size="small"
                          sx={{
                            backgroundColor: key === 'or' ? 'transparent' : '#E5E7EB',
                            color: key === 'or' ? '#6B7280' : '#1F2937',
                            fontWeight: key === 'or' ? 400 : 600,
                            fontFamily: key === 'or' ? 'inherit' : 'monospace',
                            height: key === 'or' ? 'auto' : 24,
                            border: key === 'or' ? 'none' : '1px solid #D1D5DB',
                            boxShadow: key === 'or' ? 'none' : '0 1px 2px rgba(0,0,0,0.05)',
                          }}
                        />
                      ))}
                    </Box>
                  </Stack>
                ))}
              </Stack>

              {/* Divider between categories */}
              {index < KEYBOARD_SHORTCUTS.length - 1 && (
                <Divider sx={{ mt: 2.5 }} />
              )}
            </Box>
          ))}

          {/* Tip */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: '#EEF2FF',
              borderRadius: 1,
              border: '1px solid #C7D2FE',
            }}
          >
            <Typography variant="caption" sx={{ color: '#4338CA', fontWeight: 500 }}>
              💡 Tip: Keyboard shortcuts are disabled when typing in input fields
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
});

KeyboardShortcutHelper.displayName = 'KeyboardShortcutHelper';

export default KeyboardShortcutHelper;
